// The X-Imp-Signature header.
//
// The signature covers the method, the path, a timestamp, a one-time nonce and a
// digest of the body. Each part earns its place: without the path the signature
// could be moved to a different operation, without the timestamp and nonce it could
// be replayed after being observed, and without the body digest it could be used
// with a swapped body.
//
// The component names are wire constants — changing them after the first server
// exists breaks compatibility.

import { b64uEncode } from './base64url';
import { directoryKey, keyFor, signWithDeviceKey, signWithDirectoryKey } from './device-keys';
import { sha256, utf8 } from './hash';

/**
 * Builds `id=…,time=…,nonce=…,digest=…,sig=…` for one request. [path] is the full
 * URL pathname including any prefix the server address carries; the query string is
 * not signed. A fresh nonce every call — retries re-sign.
 */
export async function signatureHeader(input: {
  accountId: string;
  method: string;
  path: string;
  body: Uint8Array;
}): Promise<string> {
  const key = await keyFor(input.accountId);
  const time = new Date().toISOString();
  const nonce = b64uEncode(crypto.getRandomValues(new Uint8Array(16)));
  const digest = b64uEncode(await sha256(input.body));
  const material = [input.method.toUpperCase(), input.path, time, nonce, digest].join('\n');
  const signature = await signWithDeviceKey(input.accountId, utf8(material));
  return [
    `id=${key.id}`,
    `time=${time}`,
    `nonce=${nonce}`,
    `digest=${digest}`,
    `sig=${b64uEncode(signature)}`,
  ].join(',');
}

/**
 * The two headers of a gated directory entry lookup: `X-Imp-Device-Signature` in the
 * exact `X-Imp-Signature` format, and `X-Imp-Device-Key` carrying the public key the
 * gate verifies against. The gate keeps no registry — the key rides along, and the
 * signature proves continuity ("the same asker as before", which is what a rate
 * budget needs), not that the asker is a real device.
 */
export async function directoryLookupHeaders(path: string): Promise<Record<string, string>> {
  const key = await directoryKey();
  const time = new Date().toISOString();
  const nonce = b64uEncode(crypto.getRandomValues(new Uint8Array(16)));
  const digest = b64uEncode(await sha256(new Uint8Array(0)));
  const material = ['GET', path, time, nonce, digest].join('\n');
  const signature = await signWithDirectoryKey(utf8(material));
  return {
    'X-Imp-Device-Signature': [
      `id=${key.id}`,
      `time=${time}`,
      `nonce=${nonce}`,
      `digest=${digest}`,
      `sig=${b64uEncode(signature)}`,
    ].join(','),
    'X-Imp-Device-Key': key.key,
  };
}
